import { Grid } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CardTitle from "@saleor/components/CardTitle";
import ClipboardNew from "@saleor/components/ClipboardNew";
import { ControlledCheckbox } from "@saleor/components/ControlledCheckbox";
import Skeleton from "@saleor/components/Skeleton";
import { AccountErrorFragment } from "@saleor/fragments/types/AccountErrorFragment";
import { maybe } from "@saleor/misc";
import { getFormErrors } from "@saleor/utils/errors";
import getAccountErrorMessage from "@saleor/utils/errors/account";
import moment from "moment-timezone";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { CustomerDetails_user } from "../../types/CustomerDetails";

const useStyles = makeStyles(
  theme => ({
    cardTitle: {
      height: "100%"
    },
    checkbox: {
      marginBottom: theme.spacing()
    },
    content: {
      paddingTop: theme.spacing()
    },
    dot: {
      backgroundColor: "#63d6b7",
      borderRadius: "100%",
      content: "''",
      display: "inline",
      height: "15px",
      marginTop: "10px",
      position: "absolute" as "absolute",
      width: "15px"
    },
    emailText: {
      marginTop: "20px"
    },
    smac: {
      fontWeight: 600,
      marginLeft: "15px",
      padding: "6px",
      paddingRight: "11px",
      width: "100%"
    },
    smacAccount: {
      display: "flex",
      justifyContent: "flex-end",
      width: "100%"
    },
    smacAlign: {
      marginTop: "15px"
    },
    smacDiv: {
      display: "flex",
      justifyContent: "flex-end"
    },
    smacLabel: {
      backgroundColor: "#c3f2e9",
      borderRadius: "25px",
      display: "inline-flex",
      paddingLeft: "10px"
    },
    subtitle: {
      marginTop: theme.spacing()
    },
    textFieldGrid: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridTemplateColumns: "1fr 1fr"
    }
  }),
  { name: "CustomerDetails" }
);

export interface CustomerDetailsProps {
  customer: CustomerDetails_user;
  data: {
    isActive: boolean;
    note: string;
  };
  disabled: boolean;
  errors: AccountErrorFragment[];
  onChange: (event: React.ChangeEvent<any>) => void;
}

const CustomerDetails: React.FC<CustomerDetailsProps> = props => {
  const { customer, data, disabled, errors, onChange } = props;
  const classes = useStyles(props);
  const intl = useIntl();

  const formErrors = getFormErrors(["note"], errors);

  return (
    <Card>
      <CardTitle
        className={classes.cardTitle}
        title={
          <>
            <Grid>
              <div className={classes.textFieldGrid}>
                <div className={classes.emailText}>
                  {maybe(() => customer.email)}
                  {customer && customer.dateJoined ? (
                    <Typography className={classes.subtitle} variant="caption">
                      <FormattedMessage
                        defaultMessage="Active member since {date}"
                        description="section subheader"
                        values={{
                          date: moment(customer.dateJoined).format("MMM YYYY")
                        }}
                      />
                    </Typography>
                  ) : (
                    <Skeleton style={{ width: "10rem" }} />
                  )}
                </div>
                <div>
                  {maybe(() =>
                    customer.smac ? (
                      <>
                        <div className={classes.smacAlign}>
                          <div className={classes.smacDiv}>
                            <div className={classes.smacLabel}>
                              <span
                                className={`${classes.smacLabel}, ${classes.dot}`}
                              ></span>
                              <span className={classes.smac}>
                                SMAC Account Holder
                              </span>
                            </div>
                          </div>
                          <div className={classes.smacAccount} id="smacIDTxt">
                            <ClipboardNew
                              label={intl.formatMessage({
                                defaultMessage: " ",
                                description: "smac id"
                              })}
                              value={maybe(() => customer.smac)}
                              onGenerate={undefined}
                            />
                          </div>
                        </div>
                      </>
                    ) : null
                  )}
                </div>
              </div>
            </Grid>
          </>
        }
      />
      <CardContent className={classes.content}>
        <ControlledCheckbox
          id="userAccountActiveCheckbox"
          checked={data.isActive}
          className={classes.checkbox}
          disabled={disabled}
          label={intl.formatMessage({
            defaultMessage: "User account active",
            description: "check to mark this account as active"
          })}
          name="isActive"
          onChange={onChange}
        />
        <TextField
          id="noteCustomerTxt"
          disabled={disabled}
          error={!!formErrors.note}
          fullWidth
          multiline
          helperText={getAccountErrorMessage(formErrors.note, intl)}
          name="note"
          label={intl.formatMessage({
            defaultMessage: "Note",
            description: "note about customer"
          })}
          value={data.note}
          onChange={onChange}
        />
      </CardContent>
    </Card>
  );
};
CustomerDetails.displayName = "CustomerDetails";
export default CustomerDetails;
